import supabase from '../utils/supabaseUtils';

// Service to handle removeBG API integration
const REMOVE_BG_API_URL = 'https://api.remove.bg/v1.0/removebg';

/**
 * Remove background from an image using remove.bg API
 * @param {File|Blob} imageFile - The image file or blob
 * @param {string} apiKey - Your remove.bg API key
 * @returns {Promise<Blob>} - Image with background removed as Blob
 */
export const removeBackground = async (imageFile, apiKey) => {
  console.log("RemoveBG Service: Starting background removal");
  
  if (!apiKey) {
    console.error("RemoveBG Service: No API key provided");
    throw new Error('API key is required for remove.bg integration');
  }

  if (!imageFile) {
    console.error("RemoveBG Service: No image file provided");
    throw new Error('Image file is required');
  }

  console.log("RemoveBG Service: Preparing request with image type:", imageFile.type, "size:", Math.round(imageFile.size / 1024), "KB");
  
  // For debugging
  console.log("Using API Key:", apiKey.substring(0, 3) + "..." + apiKey.substring(apiKey.length - 3));
  
  // This approach uses the formData method
  const formData = new FormData();
  formData.append('image_file', imageFile, 'image.jpg');
  formData.append('size', 'auto');
  formData.append('format', 'png');

  try {
    console.log("RemoveBG Service: Sending request to API");
    const response = await fetch(REMOVE_BG_API_URL, {
      method: 'POST',
      headers: {
        'X-Api-Key': apiKey
      },
      body: formData
    });

    console.log("RemoveBG Service: Received response, status:", response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error("RemoveBG API Error:", response.status, errorText);
      throw new Error('Remove.bg API error: ' + response.status + ' - ' + errorText);
    }

    // Log the response headers for debugging
    const headers = {};
    response.headers.forEach((value, key) => {
      headers[key] = value;
    });
    console.log("RemoveBG Service: Response headers:", headers);

    // Get the image as a blob
    const imageBlob = await response.blob();
    console.log("RemoveBG Service: Received image blob, type:", imageBlob.type, "size:", Math.round(imageBlob.size / 1024), "KB");
    
    return imageBlob;
  } catch (error) {
    console.error("RemoveBG Service: Error during API call:", error);
    throw error;
  }
};

/**
 * Convert a blob to a data URL
 * @param {Blob} blob - The image blob
 * @returns {Promise<string>} - Data URL
 */
export const blobToDataURL = (blob) => {
  console.log("RemoveBG Service: Converting blob to data URL");
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      console.log("RemoveBG Service: Data URL created, length:", reader.result.length);
      resolve(reader.result);
    };
    reader.onerror = (error) => {
      console.error("RemoveBG Service: Error converting blob to data URL:", error);
      reject(error);
    };
    reader.readAsDataURL(blob);
  });
};

/**
 * Convert a data URL to a file
 * @param {string} dataUrl - The data URL
 * @param {string} filename - The filename to use
 * @returns {File} - The file object
 */
export const dataURLtoFile = (dataUrl, filename) => {
  console.log("RemoveBG Service: Converting data URL to file");
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  
  const file = new File([u8arr], filename, { type: mime });
  console.log("RemoveBG Service: File created:", filename, "type:", mime, "size:", Math.round(file.size / 1024), "KB");
  return file;
};

/**
 * Process an image with remove.bg API and upload to Supabase storage
 * @param {File} imageFile - The image file to process and upload
 * @param {string} userId - The user's ID
 * @param {string} eventId - The event ID
 * @param {Array} tags - Optional tags for the image
 * @returns {Promise<Object>} - The uploaded image record
 */
export const processAndUploadImage = async (imageFile, userId, eventId, tags = null) => {
  try {
    // Check if user is authenticated
    const { data, error: authError } = await supabase.auth.getSession();
    
    if (authError) {
      console.error('Authentication error:', authError);
      throw new Error('Authentication error: ' + authError.message);
    }
    
    if (!data || !data.session) {
      console.error('No active session');
      throw new Error('No active session - please log in');
    }
    
    const session = data.session;
    
    console.log('Authenticated as:', session.user.email);
    console.log('Auth user ID:', session.user.id);
    console.log('Target user ID for upload:', userId);
    
    // Check if the authenticated user is admin/staff
    const { data: roleData, error: roleError } = await supabase
      .from('app_users')
      .select('user_type')
      .eq('id', session.user.id)
      .single();
      
    if (roleError) {
      console.error('Error checking user role:', roleError);
    } else {
      console.log('User role:', roleData?.user_type || 'unknown');
    }
    
    const removeBgApiKey = import.meta.env.VITE_REMOVE_BG_API_KEY;
    console.log('Using Remove.bg API Key:', removeBgApiKey ? 'Key is set' : 'Key is missing');
    
    if (!removeBgApiKey) {
      throw new Error('Remove.bg API key is missing');
    }

    // Create FormData for the API request with maximum quality settings
    const formData = new FormData();
    formData.append('image_file', imageFile);
    formData.append('size', 'full');         // Request full resolution output
    formData.append('format', 'png');        // PNG format for highest quality
    formData.append('channels', 'rgba');     // Include alpha channel for transparency
    formData.append('type', 'auto');         // Auto-detect image type
    formData.append('bg_color', '');         // Empty string for transparent background
    formData.append('scale', '100%');        // No downscaling
    formData.append('crop', 'false');        // Don't crop the image
    formData.append('position', 'center');   // Center the subject
    formData.append('roi', '0% 0% 100% 100%'); // Use full image as region of interest
    formData.append('add_shadow', 'false');  // No shadow for clean compositing
    formData.append('semitransparency', 'true'); // Keep semi-transparent pixels for better edges
    formData.append('bg_image_file', '');    // No background image
    
    // Log details about the image being processed
    console.log('Processing image:', imageFile.name);
    console.log('Image size:', Math.round(imageFile.size / 1024), 'KB');
    console.log('Image type:', imageFile.type);

    // Call the remove.bg API
    console.log('Calling remove.bg API with high quality settings...');
    const response = await fetch('https://api.remove.bg/v1.0/removebg', {
      method: 'POST',
      headers: {
        'X-API-Key': removeBgApiKey,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Remove.bg response:', response.status, errorText);
      throw new Error('Remove.bg API error: ' + response.status + ' - ' + errorText);
    }

    // Log response headers for debugging quality issues
    const headers = {};
    response.headers.forEach((value, key) => {
      headers[key] = value;
    });
    console.log('Remove.bg response headers:', headers);

    // Get the processed image as a blob
    const processedBlob = await response.blob();
    console.log('Processed image size:', Math.round(processedBlob.size / 1024), 'KB');
    
    // Create a file from the blob with maximum quality
    const processedFile = new File(
      [processedBlob],
      imageFile.name.split('.')[0] + '-bg-removed-hq.png',
      { type: 'image/png' }
    );
    
    // Generate a unique filename
    const fileName = userId + '_' + Date.now() + '_' + Math.floor(Math.random() * 10000) + '_hq.png';
    // Use specific folder structure
    const filePath = 'events/' + eventId + '/users/' + userId + '/' + fileName;
    
    // Upload to Supabase storage with high quality settings
    console.log('Uploading to Supabase storage with high quality settings...');
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('playmakerpics-images')
      .upload(filePath, processedFile, {
        cacheControl: '3600',
        upsert: false,
        contentType: 'image/png'
      });
      
    if (uploadError) {
      throw new Error('Storage upload failed: ' + uploadError.message);
    }
    
    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('playmakerpics-images')
      .getPublicUrl(filePath);
    
    // First try direct insert as the authenticated user should have permission
    console.log('Attempting direct insert with authenticated session...');
    
    // Test RLS policy with a query first
    try {
      const { count, error: countError } = await supabase
        .from('user_uploads')
        .select('id', { count: 'exact', head: true });
      
      if (countError) {
        console.error('RLS test query failed:', countError);
      } else {
        console.log('RLS test passed! Can query user_uploads table');
      }
    } catch (e) {
      console.error('Error testing RLS policy:', e);
    }
    
    try {
      const { data: dbData, error: dbError } = await supabase
        .from('user_uploads')
        .insert({
          user_id: userId,
          file_url: publicUrl,
          is_primary: false,
          upload_type: 'event_upload:' + eventId,
          status: 'active',
          created_at: new Date().toISOString()
        })
        .select()
        .single();
        
      if (dbError) {
        console.error('Direct insert error:', dbError);
        throw dbError;
      }
      
      console.log('Direct insert successful:', dbData);
      
      // Return in the format expected by the component
      return {
        id: dbData.id,
        user_id: userId,
        event_id: eventId, // Add this for component compatibility
        image_url: publicUrl,
        file_url: publicUrl,
        file_path: filePath,
        status: 'active',
        tags: tags, // Keep tags in memory only
        created_at: dbData.created_at,
        is_primary: dbData.is_primary,
        upload_type: dbData.upload_type
      };
    } catch (dbError) {
      console.warn('Database insert failed, using local mode instead:', dbError);
      
      // Create a mock record with the real image URL from storage
      return {
        id: 'local-' + Date.now() + '-' + Math.random().toString(36).substring(2, 15),
        user_id: userId,
        event_id: eventId,
        image_url: publicUrl, // Use the real URL from storage
        file_url: publicUrl,
        file_path: filePath,
        status: 'local',
        upload_type: 'event_upload:' + eventId,
        tags: tags,
        created_at: new Date().toISOString()
      };
    }
  } catch (error) {
    console.error("Error processing and uploading image:", error);
    throw error;
  }
};

/**
 * Fallback to local processing if remove.bg fails
 * @param {File} imageFile - The image file to process
 * @param {string} userId - The user's ID
 * @param {string} eventId - The event ID
 * @param {Array} tags - Optional tags for the image
 * @returns {Promise<Object>} - Mock image data for demo mode
 */
export const createLocalImageRecord = async (imageFile, userId, eventId, tags = null) => {
  // Create a local URL from the original file
  const imageUrl = URL.createObjectURL(imageFile);
  
  // Create a mock image record
  return {
    id: 'local-' + Date.now() + '-' + Math.random().toString(36).substring(2, 15),
    user_id: userId,
    event_id: eventId,
    image_url: imageUrl,
    file_url: imageUrl,
    status: 'local',
    upload_type: 'event_upload:' + eventId,
    tags: tags,
    created_at: new Date().toISOString()
  };
};